<template>
  <CustomModal
    :is-modal-open="isOpen"
    @toggleModal="closePopup"
    @onClickAway="clickOutside"
    :overlay="'dark'"
    :with-header="true"
    :with-footer="true"
    custom-classes="tw:h-screen tw:md:h-auto tw:w-[100vw] tw:md:max-w-[588px] tw:md:w-[588px] tw:md:rounded-md tw:rounded-none"
  >
    <template #modalHeader>
      <div class="tw:flex tw:items-center">
        <h3 class="tw:font-bold tw:text-xl tw:text-gray-800">{{ $t('rc_campaign_settings_header') }}</h3>
      </div>
    </template>
    <div class="tw:flex tw:flex-col tw:w-full">
      <PauseFlowCPAStep1
        v-if="step === 0"
        :isAdmin="hasAdminOrSupport"
        :reasons="reasons"
        :selectedReasonObject="selectedReasonObj"
        :selectedReason="selectedReason"
        :otherExplanation="otherExplanation"
        @updateOtherExplanation="updateOtherExplanation"
        @selectReason="selectReason"
      />
      <PauseFlowCPAStep2
        v-if="step === 1"
        :isAdmin="hasAdminOrSupport"
        :pauseType="pauseType"
        :isItemNetCPAEligible="isNetCpaEligible && currentCampaignType !== 'netcpa'"
        :selectedReasonObject="selectedReasonObj"
        :resumeDate="resumeDate"
        @updatePauseType="updatePauseType"
        @toggleChangeCampaignModal="toggleChangeCampaignModal"
        @setResumeDate="setResumeDate"
      />
      <PauseFlowCPAStep3
        v-if="step === 2"
        :pauseType="pauseType"
        :resumeDate="resumeDate"
        :selectedReasonObject="selectedReasonObj"
        :selectedReason="selectedReason"
        :otherExplanation="otherExplanation"
      />
      <PauseFlowCPAStep4
        v-if="step === 3"
        :feedback="feedback"
        @setFeedback="setFeedback"
        :reasonId="selectedReasonObj?.id"
      />
    </div>

    <template #modalFooter>
      <div class="tw:flex tw:flex-col tw:md:flex-row tw:md:justify-end tw:w-full tw:gap-y-6">
        <GhostButton @click="closePopup" :disabled="isLoading" class="tw:order-1 tw:md:order-0">
          {{ $t(cancelButtonLabel) }}
        </GhostButton>
        <DangerButton v-show="step === 2" @click="nextStep" class="tw:md:ml-6" :disabled="isNextActionDisabled">
          <span v-show="!isLoading">{{ $t(nextButtonLabel) }}</span>
          <span v-show="isLoading" class="tw:px-4"><SmallDotLoader color="#fff" /></span>
        </DangerButton>
        <MainButton v-show="step !== 2" @click="nextStep" class="tw:md:ml-6" :disabled="isNextActionDisabled">
          <span v-show="!isLoading">{{ $t(nextButtonLabel) }}</span>
          <span v-show="isLoading" class="tw:px-4"><SmallDotLoader color="#fff" /></span>
        </MainButton>
      </div>
    </template>
  </CustomModal>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'

// Component imports
import CustomModal from '@/components/base/CustomModal.vue'
import GhostButton from '@/components/base/buttons/GhostButton.vue'
import MainButton from '@/components/base/buttons/MainButton.vue'
import DangerButton from '@/components/base/buttons/DangerButton.vue'
import SmallDotLoader from '@/components/base/loaders/SmallDotLoader.vue'
import PauseFlowCPAStep1 from '@/components/rateConnect/pauseFlow/cpa/PauseFlowCPAStep1.vue'
import PauseFlowCPAStep2 from '@/components/rateConnect/pauseFlow/cpa/PauseFlowCPAStep2.vue'
import PauseFlowCPAStep3 from '@/components/rateConnect/pauseFlow/cpa/PauseFlowCPAStep3.vue'
import PauseFlowCPAStep4 from '@/components/rateConnect/pauseFlow/cpa/PauseFlowCPAStep4.vue'

// Utilities and composables
import useToastNotifications from '@/components/notifications/useToastNotifications'
import appAnalytics from '@/utils/tracking'
import * as Sentry from '@sentry/vue'
import { reasons } from '@/constants/rcPauseReasons.js'
import { useCampaign, useCampaignMutation, usePartners } from '@/components/rateConnect/queries'
import { selectedAccommodationId } from '@/layouts/queries'
import { useRateConnect } from '@/components/rateConnect/useRateConnect'
import { useQueryClient } from '@tanstack/vue-query'
import { useIsMobile } from '@/composables/useIsMobile'
import { useSessionValues } from '@/composables/useSessionValues'

// ============================================================================
// PROPS & EMITS
// ============================================================================
const props = defineProps({
  isOpen: Boolean,
})

const emit = defineEmits(['toggleChangeCampaignModal'])

// ============================================================================
// COMPOSABLES & EXTERNAL DATA
// ============================================================================
const store = useStore()
const queryClient = useQueryClient()
const { isMobile } = useIsMobile()
const { hasAdminOrSupport } = useSessionValues()
const { displayNotification, showSaveError } = useToastNotifications()
const { currentCampaignType, campaignsData } = useCampaign(selectedAccommodationId)
const { isNetCpaEligible } = usePartners(selectedAccommodationId)
const { pauseCampaign, deactivateCampaign } = useCampaignMutation()
const { togglePauseCampaign, isEditingResumeDate } = useRateConnect()

// ============================================================================
// STATE (REFS)
// ============================================================================
const step = ref(0)
const hintsVisible = ref(false)
const pauseType = ref('temporary')
const resumeDate = ref(null)
const selectedReason = ref(null)
const selectedReasonObj = ref(null)
const otherExplanation = ref('')
const feedback = ref('')
const isLoading = ref(false)

// ============================================================================
// SESSION STORAGE FUNCTIONS
// ============================================================================
const restoreFlowState = () => {
  const saved = sessionStorage.getItem('pauseFlowState')
  if (!saved) return false

  try {
    const state = JSON.parse(saved)

    // Optional: Check if not too old (e.g., 1 hour)
    const ONE_HOUR = 3600000
    if (Date.now() - state.timestamp > ONE_HOUR) {
      sessionStorage.removeItem('pauseFlowState')
      return false
    }

    // Restore state
    selectedReason.value = state.selectedReason
    selectedReasonObj.value = state.selectedReasonObj
    pauseType.value = state.pauseType
    resumeDate.value = state.resumeDate
    if (resumeDate.value?.fullDate) {
      resumeDate.value = {
        ...resumeDate.value,
        fullDate: new Date(resumeDate.value.fullDate),
      }
    }
    otherExplanation.value = state.otherExplanation
    step.value = state.step
    hintsVisible.value = state.hintsVisible

    // Clear sessionStorage after restoring
    sessionStorage.removeItem('pauseFlowState')

    return true
  } catch {
    sessionStorage.removeItem('pauseFlowState')
    return false
  }
}

// ============================================================================
// COMPUTED PROPERTIES
// ============================================================================
const isNextActionDisabled = computed(() => {
  if (hasAdminOrSupport.value) {
    if (
      (step.value === 0 && selectedReason.value === null) ||
      (step.value === 0 && selectedReasonObj.value?.id === 11 && otherExplanation.value === '') ||
      (step.value === 1 && !pauseType.value) ||
      ((step.value === 1 || step.value === 2) && !selectedReasonObj.value) ||
      (step.value === 1 && pauseType.value === 'temporary' && !resumeDate.value) ||
      (step.value === 3 && feedback.value.length < 3) ||
      isLoading.value
    ) {
      return true
    } else return false
  } else return false
})

const cancelButtonLabel = computed(() => {
  if (isEditingResumeDate.value) return 'cancel'
  if (step.value === 0 || step.value === 1 || step.value === 2) return 'rc_pause_flow_keep_rates_cta'
  if (step.value === 3) return 'rc_pause_flow_feedback_no_cta'
  return 'close'
})

const nextButtonLabel = computed(() => {
  if (hasAdminOrSupport.value) {
    if (step.value === 0 || step.value === 1) return 'rc_pause_flow_continue'
    if (step.value === 2 && pauseType.value === 'temporary') return 'rc_deactivate_campaign_button'
    if (step.value === 2 && pauseType.value === 'permanent') return 'rc_set_campaign_inactive_cta'
    if (step.value === 3) return 'rc_pause_flow_feedback_send_cta'
  } else {
    return 'rc_admin_log_in_cta'
  }
  return ''
})

// ============================================================================
// WATCHERS
// ============================================================================
watch(
  () => props.isOpen,
  isOpen => {
    if (!isOpen) return

    // First, try to restore from sessionStorage
    const restored = restoreFlowState()

    // If nothing restored, check for editing resume date
    if (
      !restored &&
      campaignsData.value?.status === 'PAUSED_UNTIL' &&
      campaignsData.value?.endDate &&
      isEditingResumeDate.value
    ) {
      step.value = 1
      pauseType.value = 'temporary'
      const date = new Date(campaignsData.value.endDate)
      resumeDate.value = {
        day: date.getDate(),
        month: date.getMonth(),
        year: date.getFullYear(),
        fullDate: date,
      }
      selectedReason.value = 'edit-resume-date'
      selectedReasonObj.value = {
        id: 0,
        value: 'editing_resume_date',
        group: 'system',
      }
    }
  },
  { immediate: true }
)

// ============================================================================
// FUNCTIONS
// ============================================================================
const selectReason = reason => {
  hintsVisible.value = true
  const reasonGroup = reason.split('-')[0].trim()
  const reasonId = Number(reason.split('-')[1].trim())
  selectedReason.value = reason
  reasons.forEach(reason => {
    if (reason.group.value === reasonGroup) {
      selectedReasonObj.value = reason.options.find(item => item.id === reasonId)
    }
  })
  appAnalytics.track(appAnalytics.events.RC_PAUSE_CAMPAIGN_REASON_SELECTED, {
    reason: selectedReasonObj.value?.value,
    accommodationId: selectedAccommodationId.value,
    campaignType: currentCampaignType.value,
    isNetCPAEligible: isNetCpaEligible.value,
  })
}

const toggleChangeCampaignModal = () => {
  appAnalytics.track(appAnalytics.events.RC_PAUSE_CAMPAIGN_CHANGE_CAMPAIGN_TYPE, {
    reason: selectedReasonObj.value?.value,
    accommodationId: selectedAccommodationId.value,
    campaignType: currentCampaignType.value,
    isNetCPAEligible: isNetCpaEligible.value,
  })
  emit('toggleChangeCampaignModal', 'pauseFlow')
  resetModal()
}

const setResumeDate = value => {
  const { year, month, day } = value
  const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`

  appAnalytics.track(appAnalytics.events.RC_PAUSE_CAMPAIGN_SET_DATE_UNTIL, {
    reason: selectedReasonObj.value?.value,
    accommodationId: selectedAccommodationId.value,
    campaignType: currentCampaignType.value,
    resumeDate: dateString,
  })
  resumeDate.value = value
}

const setFeedback = text => {
  feedback.value = text
}

const updatePauseType = type => {
  pauseType.value = type
}

const updateOtherExplanation = text => {
  otherExplanation.value = text
}

const nextStep = () => {
  const dateString = resumeDate.value
    ? `${resumeDate.value.year}-${String(resumeDate.value.month + 1).padStart(2, '0')}-${String(resumeDate.value.day).padStart(2, '0')}`
    : null

  if (hasAdminOrSupport.value) {
    // Step 0 -> 1: Select reason
    if (step.value === 0) {
      appAnalytics.track(appAnalytics.events.RC_PAUSE_CAMPAIGN_CONTINUE_BUTTON_ON_REASON_SELECTION_CLICKED, {
        reason: selectedReasonObj.value?.value,
        reasonId: selectedReasonObj.value?.id,
        accommodationId: selectedAccommodationId.value,
        campaignType: currentCampaignType.value,
        isNetCPAEligible: isNetCpaEligible.value,
      })
      step.value = 1

      // Force permanent for reasons 9 & 10
      if (selectedReasonObj.value?.id === 9 || selectedReasonObj.value?.id === 10) {
        pauseType.value = 'permanent'
      }
      return
    }

    // Step 1 -> 2: Select pause type
    if (step.value === 1) {
      appAnalytics.track(appAnalytics.events.RC_PAUSE_CAMPAIGN_CONTINUE_BUTTON_ON_PAUSE_TYPE_SELECTION_CLICKED, {
        accommodationId: selectedAccommodationId.value,
        campaignType: currentCampaignType.value,
        pauseTypeSelected: pauseType.value,
        resumeDate: pauseType.value === 'temporary' ? dateString : null,
      })
      step.value = 2
      return
    }

    // Step 2: Execute action
    if (step.value === 2) {
      if (pauseType.value === 'permanent') {
        onDeactivateCampaign()
      } else {
        onPauseCampaign()
      }
      return
    }

    // Step 3: Send feedback
    if (step.value === 3 && feedback.value.length > 2) {
      if (!selectedReasonObj.value?.value) {
        Sentry.withScope(scope => {
          scope.setTag('feature', 'RC_PauseFlow')
          Sentry.captureException('[PauseFlowCPAModal] Missing selected reason for RC_PAUSE_CAMPAIGN_SEND_FEEDBACK')
        })
        return
      }
      appAnalytics.track(appAnalytics.events.RC_PAUSE_CAMPAIGN_SEND_FEEDBACK, {
        reason: selectedReasonObj.value?.value,
        accommodationId: selectedAccommodationId.value,
        campaignType: currentCampaignType.value,
        isNetCPAEligible: isNetCpaEligible.value,
        feedback: feedback.value,
      })
      resetModal()
      return
    }
  } else {
    appAnalytics.track(appAnalytics.events.RC_LOGIN_AS_ADMIN_CLICKED, {
      accommodationId: selectedAccommodationId.value,
    })
    store.dispatch('session/logout', '/rate-connect/running/performance?loginAsAdmin=true')
  }
}

const clickOutside = () => {
  if (isMobile.value) return
  else closePopup()
}

const closePopup = () => {
  if (step.value === 0) {
    appAnalytics.track(appAnalytics.events.RC_PAUSE_UNTIL_FLOW_CANCELLED, {
      reason: selectedReasonObj.value?.value,
      accommodationId: selectedAccommodationId.value,
      campaignType: currentCampaignType.value,
      isNetCPAEligible: isNetCpaEligible.value,
      step: 'select-reason',
    })
  } else if (step.value === 1) {
    appAnalytics.track(appAnalytics.events.RC_PAUSE_UNTIL_FLOW_CANCELLED, {
      reason: selectedReasonObj.value?.value,
      accommodationId: selectedAccommodationId.value,
      campaignType: currentCampaignType.value,
      isNetCPAEligible: isNetCpaEligible.value,
      step: 'select-campaign-type',
    })
  } else if (step.value === 2) {
    appAnalytics.track(appAnalytics.events.RC_PAUSE_UNTIL_FLOW_CANCELLED, {
      reason: selectedReasonObj.value?.value,
      pauseType: pauseType.value,
      accommodationId: selectedAccommodationId.value,
      campaignType: currentCampaignType.value,
      isNetCPAEligible: isNetCpaEligible.value,
      step: 'pause-campaign',
    })
  } else if (step.value === 3) {
    appAnalytics.track(appAnalytics.events.RC_PAUSE_CAMPAIGN_NO_FEEDBACK, {
      reason: selectedReasonObj.value?.value,
      accommodationId: selectedAccommodationId.value,
      campaignType: currentCampaignType.value,
      isNetCPAEligible: isNetCpaEligible.value,
    })
  }
  resetModal()
}

const resetModal = () => {
  pauseType.value = 'temporary'
  resumeDate.value = null
  selectedReason.value = null
  selectedReasonObj.value = null
  step.value = 0
  hintsVisible.value = false
  otherExplanation.value = ''
  feedback.value = ''

  togglePauseCampaign('close', false)
}

const onPauseCampaign = () => {
  if (!selectedAccommodationId.value) return
  if (!selectedReasonObj.value?.value || !selectedReasonObj.value?.group) {
    Sentry.withScope(scope => {
      scope.setTag('feature', 'RC_PauseFlow')
      Sentry.captureException('[PauseFlowCPAModal] Missing selected reason for in-flow pause event')
    })
    return
  } else {
    appAnalytics.track(appAnalytics.events.RC_RED_PAUSE_CAMPAIGN_BUTTON_CLICKED, {
      reasonGroup: selectedReasonObj.value?.group,
      reason:
        selectedReasonObj.value?.id === 11
          ? `${selectedReasonObj.value?.value}: ${otherExplanation.value}`
          : selectedReasonObj.value?.value,
      accommodationId: selectedAccommodationId.value,
      campaignType: currentCampaignType.value,
      pauseType: 'pause_temporary',
    })
  }

  const { year, month, day } = resumeDate.value
  const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
  isLoading.value = true
  pauseCampaign(
    {
      accommodationId: selectedAccommodationId.value,
      pauseUntil: dateString,
    },
    {
      onSuccess: () => {
        isLoading.value = false
        resetModal()
        queryClient.invalidateQueries({
          queryKey: ['RCCampaigns', selectedAccommodationId.value],
        })
        displayNotification({
          title: 'setting_updated_success',
          titleTranslationKey: true,
          message: 'rc_deactivate_campaign_success_message',
          isTranslationKey: true,
          type: 'info',
        })
      },
      onError: () => {
        isLoading.value = false
        showSaveError()
      },
    }
  )
}

const onDeactivateCampaign = () => {
  if (!selectedAccommodationId.value) return
  if (!selectedReasonObj.value?.value || !selectedReasonObj.value?.group) {
    Sentry.withScope(scope => {
      scope.setTag('feature', 'RC_PauseFlow')
      Sentry.captureException('[PauseFlowCPAModal] Missing selected reason for in-flow pause event')
    })
    return
  } else {
    appAnalytics.track(appAnalytics.events.RC_RED_PAUSE_CAMPAIGN_BUTTON_CLICKED, {
      reasonGroup: selectedReasonObj.value?.group,
      reason:
        selectedReasonObj.value?.id === 11
          ? `${selectedReasonObj.value?.value}: ${otherExplanation.value}`
          : selectedReasonObj.value?.value,
      accommodationId: selectedAccommodationId.value,
      campaignType: currentCampaignType.value,
      pauseType: 'deactivation',
    })
  }
  isLoading.value = true

  deactivateCampaign(
    {
      accommodationId: selectedAccommodationId.value,
    },
    {
      onSuccess: () => {
        isLoading.value = false
        displayNotification({
          title: 'setting_updated_success',
          titleTranslationKey: true,
          message: 'rc_pause_success_message',
          isTranslationKey: true,
          type: 'info',
        })
        step.value = 3
      },
      onError: () => {
        isLoading.value = false
        showSaveError()
      },
    }
  )
}
</script>
